import { ERoleCode, ESTATES, EUserStatusCode, User } from './user';
import { UserCalendarsEnum, UserIntegrationsStatusEnum } from './user-profile';
import { EWidgetGoal } from './widget';

export interface UserSettingsList
  extends Omit<
    User,
    | 'id'
    | 'status'
    | 'agent'
    | 'agentId'
    | 'role'
    | 'team'
    | 'firstName'
    | 'lastName'
    | 'licenseNumber'
    | 'licenseStates'
    | 'email'
    | 'jobTitle'
    | 'forwardingEmail'
    | 'forwardingPhone'
    | 'phone'
    | 'extPhone'
    | 'faxPhone'
    | 'businessPhone'
    | 'password'
    | 'forwardingPhoneLastVerificationDate'
    | 'forwardingEmailLastVerificationDate'
    | 'emailLastVerificationDate'
  > {
  id: string;
  status: EUserStatusCode | null;
  agentId: string | null;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  phone: string | null;
  businessPhone?: string | null;
  extPhone: string | null;
  faxPhone: string | null;
  licenseNumber: string | null;
  licenseStates: ESTATES[] | null;
  team: string | null;
  forwardingEmail: string | null;
  forwardingPhone: string | null;
  email: string | null;
  password: string | null;
  notifications: NotificationPreference[];
  // Isolated channels selection for user notifications
  notificationChannels?: NotificationChannel[];
  // Per-user master switch for mobile push. Gates ONLY the Expo push send —
  // email/SMS/in-app copies are unaffected. Optional here for back-compat while
  // the backend column deploys; treat a missing value as true (enabled).
  inAppMobileNotification?: boolean;
  isForwardingPhoneValid: boolean;
  forwardingPhoneLastVerificationDate: Date | null;
  isForwardingEmailValid: boolean;
  forwardingEmailLastVerificationDate: Date | null;
  isEmailValid: boolean;
  emailLastVerificationDate: Date;
  emailSignature: EmailSignature;
  isGoogleSynced?: boolean;
  googleEmail?: string;
  googleSyncStatus: UserIntegrationsStatusEnum;
  googleSyncErrorMessage?: string | null;
  isZoomSynced?: boolean;
  zoomEmail?: string;
  zoomSyncStatus: UserIntegrationsStatusEnum;
  zoomSyncErrorMessage?: string | null;
  isMicrosoftSynced?: boolean;
  microsoftEmail?: string;
  microsoftSyncStatus: UserIntegrationsStatusEnum;
  microsoftSyncErrorMessage?: string | null;
  appointmentSetter?: boolean;
  assistantAiLoanOfficerBio?: string;
  assistantAiCompanyBio?: string;
  assistantAiProducts?: string;
  assistantAiInstructions?: string;
  assistantName?: string;
  aiReplyDelayEnabled?: boolean;
  aiReplyDelaySeconds?: number;
  aiAutoReplyToUnknownNumbers?: boolean;
  assistantLinks?: AssistantLink[];
  aiGuardrails?: AiGuardrails;
  assistantAiAutoApprove?: boolean;
  defaultOutboundEmail: string;
  defaultCalendar: UserCalendarsEnum | null;
  ariveEmail?: string;
  timezone?: string | null;
  allowEventConflicts?: boolean;
  useLoaiEmailAsFallback?: boolean;
  // A2P fields
  a2pBusinessWebsite?: string;
  a2pBusinessName?: string;
  a2pRegionOfOperation?: string;
  a2eEin?: string;
  a2pLegalEntityType?: string;
  a2pAddress?: string;
  color?: string;
  // Custom AI widget title shown in the widget header
  title?: string | null;
}

/**
 * Mirrors IAiGuardrails on the server. The platform's unconditional
 * protections (opt-out, moderation, automation-loop, PII redaction) are
 * deliberately absent — they are not per-user settings and must not be
 * representable as ones.
 */
export interface AiGuardrails {
  /**
   * @deprecated Ignored since 2026-08-26 — RESPA/TILA is enforced
   * unconditionally and is listed under ALWAYS ON. Kept only because objects
   * saved before then still carry the key.
   */
  respaMode?: boolean;
  allowAvgRates?: boolean;
  requireRateDisclosure?: boolean;
  restrictedTopics?: string;
  disclaimerText?: string;
  autoEscalate?: boolean;
  escalationTriggers?: string;
}

export interface AssistantLink {
  label: string;
  url: string;
  /** Set when the LO opts this row into smart tracking. */
  triggerLinkId?: string;
  /** Short URL resolved from the trigger link; shared in place of `url`. */
  trackedUrl?: string;
}

export interface UserSettingsForm extends Omit<UserSettingsList, 'roles'> {
  roles: ERoleCode[];
  goal?: EWidgetGoal;
  color?: string;
  title?: string | null;
}

export interface NotificationPreference {
  type: ENotificationsType;
  value: ENotificationsValue;
  // Optional until all forms adopt channels selection
  channels?: NotificationChannel[];
}

export enum ENotificationsType {
  MESSAGES_REPLIES = 'MESSAGES_REPLIES', // Notification of incoming messages and emails
  MESSAGES_LIMIT = 'MESSAGES_LIMIT', // Notification if messages limit is hit
  INCOMING_CALL = 'INCOMING_CALL', // Notification of calls
  EMAILS_LIMIT = 'EMAILS_LIMIT', // Notification if emails limit is hit
  NOTE_ADDED = 'NOTE_ADDED', // Notification of added note
  NOTE_MENTION = 'NOTE_MENTION', // When someone @mentions you in a note
  SENT_EMAIL_FAILED = 'SENT_EMAIL_FAILED', // Notification of failed email
  EVENT_CREATION = 'EVENT_CREATION', // Notification for calendar event
  RATE_WATCH_ACHIEVED = 'RATE_WATCH_ACHIEVED', // When rate watch hits the target rate
  // The nightly rate-watch cron has sent ONLY this batched type since 2026-03-23; the
  // per-contact RATE_WATCH_ACHIEVED above is kept for the rows written before that.
  RATE_WATCH_ACHIEVED_SUMMARY = 'RATE_WATCH_ACHIEVED_SUMMARY', // One row per owner listing every target hit in the run
  // Target rate reached but the borrower was deliberately NOT contacted: the
  // loan is inside its post-closing early-payoff window, or Rate Watch is
  // switched off for the contact (Rec0BRBJVRSQ6). Separate from the SUMMARY
  // above because that one deep-links on `completedAt`, which a withheld hit
  // never sets — reusing it would link the officer to an empty list.
  RATE_WATCH_SUPPRESSED_SUMMARY = 'RATE_WATCH_SUPPRESSED_SUMMARY',
  SUPPORT_TICKET_UPDATED = 'SUPPORT_TICKET_UPDATED', // When support ticket is updated
  HELP_CENTER_CREATED = 'HELP_CENTER_CREATED', // When help center is created
  INTEGRATION_REQUEST_UPDATED = 'INTEGRATION_REQUEST_UPDATED', // When integration request is updated
  JOURNEY_MESSAGES_PENDING = 'JOURNEY_MESSAGES_PENDING', // When journey messages are pending
  GOOGLE_CALENDAR_CONNECTION_FAILED = 'GOOGLE_CALENDAR_CONNECTION_FAILED', // When Google Calendar connection fails
  OUTLOOK_CALENDAR_CONNECTION_FAILED = 'OUTLOOK_CALENDAR_CONNECTION_FAILED', // When Outlook Calendar connection fails
  ZOOM_CONNECTION_FAILED = 'ZOOM_CONNECTION_FAILED', // When Zoom connection fails
  PURCHASE_TOKENS_SUCCESS = 'PURCHASE_TOKENS_SUCCESS', // When tokens are purchased
  AUTO_RECHARGE_TOKENS_SUCCESS = 'AUTO_RECHARGE_TOKENS_SUCCESS', // When tokens are auto-recharged
  PURCHASE_TOKENS_FAILED = 'PURCHASE_TOKENS_FAILED', // When tokens purchase fails
  AUTO_RECHARGE_TOKENS_FAILED = 'AUTO_RECHARGE_TOKENS_FAILED', // When tokens auto-recharge fails
  INSUFFICIENT_BALANCE = 'INSUFFICIENT_BALANCE', // When tokens are insufficient
  LOW_BALANCE = 'LOW_BALANCE', // When tokens are low
  SMS_BLOCKED = 'SMS_BLOCKED', // When SMS is blocked
  EMAIL_BLOCKED = 'EMAIL_BLOCKED', // When email is blocked
  EMAIL_SENDING_PAUSED = 'EMAIL_SENDING_PAUSED', // Rec0C5U0G582W: the user's Outlook/Gmail hit its sending limit; waiting emails resume automatically
  WALLET_SUSPENDED = 'WALLET_SUSPENDED', // When wallet is suspended
  REFERRAL_REWARD = 'REFERRAL_REWARD', // An owner approved a referral and its tokens were granted
  CONTACT_OWNER_CHANGED = 'CONTACT_OWNER_CHANGED',
  // A lead ENTERED the system already owned by this user (integration, web
  // form, chat widget, booking, inbound call/SMS). Distinct from
  // CONTACT_OWNER_CHANGED: round robin often re-picks the existing owner, so
  // nothing changes hands and that event stays silent. Rec0BPNKA5MR6.
  NEW_LEAD_RECEIVED = 'NEW_LEAD_RECEIVED',
  CONTACT_FOLLOWER_ADDED = 'CONTACT_FOLLOWER_ADDED',
  USER_DELETED = 'USER_DELETED', // When a user is deleted (success or failure)
  TASK_ASSIGNED = 'TASK_ASSIGNED', // When a calendar task/event is assigned to a user
  PROPERTY_PULSE_VALIDATION_PENDING_SUMMARY = 'PROPERTY_PULSE_VALIDATION_PENDING_SUMMARY', // Daily per-owner summary of PropertyPulse reports awaiting manual validation
  // Idea Board (wire values are UPPERCASE, matching backend src/user/constants.ts)
  IDEA_STATUS_CHANGED = 'IDEA_STATUS_CHANGED', // When an owner moves an idea to a new status
  IDEA_SHIPPED = 'IDEA_SHIPPED', // When an idea is shipped (Done) — sent to the submitter + upvoters
  IDEA_COMMENTED = 'IDEA_COMMENTED', // When someone comments on an idea
  BIRTHDAY_MESSAGE_PENDING_APPROVAL = 'BIRTHDAY_MESSAGE_PENDING_APPROVAL', // When an AI birthday message suggestion is pending the owner's approval
  POWER_DIAL_QUEUE_SAVED = 'POWER_DIAL_QUEUE_SAVED', // Power Dialer §13b: a session ended and the contacts it never reached were saved as a list
  // Activity on a file a teammate follows. NOTE: the wire value is lowercase to match the backend
  // (src/user/constants.ts), unlike the other (UPPERCASE) members above.
  FOLLOWER_FILE_ACTIVITY = 'follower_file_activity'
}

export enum ENotificationsValue {
  IMMEDIATELY = 'IMMEDIATELY',
  HOURLY = 'HOURLY',
  EVERY_24_HOURS = 'EVERY_24_HOURS',
  DISABLED = 'DISABLED'
}

export type NotificationChannel = 'email' | 'sms';

export type EmailVerificationType = 'MAIN' | 'FORWARDING';

export interface UserRelationsInfo {
  contacts: number;
  mortgages: number;
  loans: number;
}

export enum SignatureType {
  CUSTOM_HTML = 'custom_html',
  SYSTEM_DEFAULT = 'system_default'
}

export interface EmailSignature {
  companyRepresentative: string | null;
  email: string | null;
  colors: string | null;
  socialLinks: SocialLinks[];
  footerText: string | null;
  html: string | null;
  phone: string | null;
  signatureType: SignatureType;
  customHtml?: string | null;
}

export interface SocialLinks {
  type?: ESocialType;
  link?: string;
}

/**
 * A social row not filled in yet: empty, or still just the "https://" that "Add Item"
 * starts it with. It is not an error, and it is not shown in the signature.
 */
export const isBlankSocialLink = (link?: string | null) => !link || /^\s*(https?:\/\/)?\s*$/i.test(link);

export enum ESocialType {
  LINKED_IN = 'LinkedIn',
  YOU_TUBE = 'YouTube',
  FACEBOOK = 'Facebook',
  APPLY_LINK = 'Apply Link',
  OTHER = 'Other'
}

export enum ENotificationActionView {
  CONTACT = 'CONTACT',
  CALENDAR = 'CALENDAR',
  AI_ASSISTANT = 'AI_ASSISTANT'
}

export enum ENotificationActionComponent {
  CONVERSATIONS = 'CONVERSATIONS',
  CALENDAR = 'CALENDAR',
  PROPERTY_PULSE = 'PROPERTY_PULSE'
}

export interface Notification {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
  title: string;
  type: ENotificationsType;
  timeToSend: Date;
  content: string;
  read: boolean;
  sent: boolean;
  taskId: string;
  action: NotificationAction;
  userId: string;
  companyId: string;
  quantity?: number;
  childNotificationIds?: string[];
}

export interface NotificationAction {
  view: ENotificationActionView;
  values: NotificationActionValues;
  component: ENotificationActionComponent;
}

export interface NotificationActionValues {
  // Historically these four were typed as required, but most notification
  // producers do not set all of them (e.g. PROPERTY_PULSE_VALIDATION_PENDING_SUMMARY
  // only carries totalCount + reports; LOW_BALANCE only carries companyId).
  // Marking them optional matches the runtime shape and prevents consumers
  // from destructuring `undefined` thinking the type guaranteed a value.
  companyId?: string;
  contactId?: string;
  profileUrl?: string;
  contactName?: string;
  ticketId?: string;
  helpCenterId?: string;
  type?: string;
  integrationId?: string;
  isPartner?: boolean;
  // NOTE_MENTION: the note that named you, so the notes tab can scroll to it
  // rather than just opening the file.
  noteId?: string;
  // Optional prebuilt deep-link (e.g. FOLLOWER_FILE_ACTIVITY may carry it directly).
  link?: string;
  // RATE_WATCH_ACHIEVED_SUMMARY: start of the cron run this summary describes, so
  // the deep link can show that run's hits instead of the whole sticky `completed`
  // backlog. Absent on rows written before it shipped.
  completedFrom?: string;
  eventTitle?: string;
  eventStartDate?: string;
  eventEndDate?: string;
  // PropertyPulse pending-validation summary payload
  totalCount?: number;
  reports?: PropertyPulseValidationSummary[];
  // Owner `totalCount` was scoped to. The target screen MUST be opened filtered to this
  // owner — an admin's default Property Pulse view is company-wide, so without it the
  // list is longer than the count promised (Rec0BHE7D2K4L).
  ownerId?: string;
  // Power Dialer §13b: the session whose leftover queue was saved, the name it was
  // saved under, and the smart list to open. `powerDialSmartListId` is absent when the
  // backend wrote the tag but could not write the list — the name still identifies it.
  powerDialSessionId?: string;
  listName?: string;
  powerDialSmartListId?: string;
}

/**
 * One row in the PropertyPulse pending-validation summary notification payload.
 *
 * Emitted by the backend daily cron in `PropertyPulseValidationTasksService`.
 * Each entry represents a PropertyPulse report awaiting manual validation by
 * the contact owner — clicking the parent notification routes to
 * `/pages/ai-assistant?tab=property_pulse` where the owner can review the
 * full report and resolve the loan-data conflict.
 *
 * The notification carries an array of these (capped by per-owner backlog
 * size) inside `NotificationActionValues.reports`, alongside `totalCount`.
 */
export interface PropertyPulseValidationSummary {
  reportId: string;
  contactId: string;
  contactName: string;
}

export type NotificationTab = 'all' | 'unread';

export interface VoicemailDrop {
  id: string;
  name: string;
  fileName: string;
  /**
   * Time-limited presigned URL generated by the backend on every read.
   * Do NOT cache this — it expires (1 hour TTL).
   */
  audioUrl: string;
  duration: number | null;
  mimeType: string | null;
  createdAt: string;
  updatedAt: string;
}
